import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getClassById, getQuizByClass, submitQuiz } from "../services/api";
import { useAuth } from "../context/AuthContext";
import Sidebar    from "../components/Sidebar";
import QuizViewer from "../components/QuizViewer";
import ChatWidget from "../components/ChatWidget";
import PageHero   from "../components/PageHero";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-PE", {
    year: "numeric", month: "long", day: "numeric",
  });
}

export default function ClassDetail() {
  const { id }                = useParams();
  const navigate              = useNavigate();
  const { user }              = useAuth();
  const [clase,   setClase]   = useState(null);
  const [quiz,    setQuiz]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // ── Estado compartido quiz → chat ──────────────────────────────
  const [debateAnswers, setDebateAnswers] = useState(null);
  const [debateResults, setDebateResults] = useState(null);
  const [chatAutoOpen,  setChatAutoOpen]  = useState(false);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([getClassById(id), getQuizByClass(id)])
      .then(([detail, quizData]) => { setClase(detail); setQuiz(quizData); })
      .catch(() => setError("No se pudo cargar la clase."))
      .finally(() => setLoading(false));
  }, [id]);

  const handleQuizSubmit = (answers) => submitQuiz({ quiz_id: quiz.id, answers });

  const handleStartDebate = (answers, results) => {
    setDebateAnswers(answers);
    setDebateResults(results);
    setChatAutoOpen(true);
  };

  return (
    <div className="page">
      <PageHero
        section="clases"
        title={loading ? "Cargando..." : (clase?.title ?? "Clase")}
        backBtn={{ label: "Volver", onClick: () => navigate("/clases") }}
      />

      <div className="two-col">
        <main>
          {error && <p style={{ color: "#c0392b" }}>{error}</p>}

          {loading && (
            <div className="skel">
              {[65, 35, 100, 95, 88, 70].map((w, i) => (
                <div key={i} className="skel-line"
                  style={{ width: `${w}%`, height: i === 0 ? 18 : 12, marginBottom: 8 }} />
              ))}
            </div>
          )}

          {!loading && clase && (
            <>
              <div className="card">
                <h2 style={{
                  fontFamily: "var(--font-display)", fontWeight: 800,
                  fontSize: "2rem", color: "var(--teal-dark)",
                  marginBottom: ".4rem", lineHeight: 1.35,
                }}>
                  {clase.title}
                </h2>
                {clase.created_at && (
                  <p style={{
                    fontSize: "0.75rem", color: "var(--text-light)",
                    marginBottom: "1rem", borderBottom: "0.5px solid var(--border)",
                    paddingBottom: ".8rem",
                  }}>
                    Publicado el {formatDate(clase.created_at)}
                    {clase.author_name && ` · ${clase.author_name}`}
                  </p>
                )}
                <div
                  style={{ fontSize: "0.92rem", color: "var(--text)", lineHeight: 1.85 }}
                  dangerouslySetInnerHTML={{ __html: clase.content ?? clase.description }}
                />
              </div>

              {quiz && user && (
                <QuizViewer
                  quiz={quiz}
                  deadline={quiz.deadline ?? null}   // ← fecha límite del quiz
                  onSubmit={handleQuizSubmit}
                  onStartDebate={handleStartDebate}
                  isAdmin={isAdmin}
                />
              )}
            </>
          )}
        </main>

        <Sidebar activeClassId={id} />
      </div>

      {!loading && clase && (
        <ChatWidget
          pageType="class"
          title={clase.title ?? ""}
          content={clase.content ?? clase.description ?? ""}
          quizData={quiz}
          debateAnswers={debateAnswers}
          debateResults={debateResults}
          autoOpen={chatAutoOpen}
        />
      )}
    </div>
  );
}